/* Corporate gifting — recipient-facing redeem page for addressless campaigns.

   Reached from the invite email as /pages/corporate-addressless-gifting?token=<uuid>. The
   recipient picks one of the campaign's gift options, enters a US address and
   a ship date, and confirms. Screens: welcome -> choose -> detail -> details
   -> review -> done, plus expired / already-redeemed / error.

   API (public, rate-limited, PII-light — corporate/campaigns.js):

     GET  {api_base}/api/corporate/redeem/:token
       { ok, redemption: {
           state: 'invited' | 'redeemed' | 'expired' | 'closed',
           recipient: { first_name, last_name?, email? },
           campaign: { company, company_logo_url?, sender_name?, gift_message?, redeem_deadline? },
           options: [{ key, title, subtitle?, description?, image_url, images?, badge?,
                       items?: [{ title, quantity, sku?, handle?, image_url? }] }],
           allowed_ship_dates: ['YYYY-MM-DD'],
           redeemed?: { option_key, option_title, ship_date, address, order_status, tracking_url }
       } }

   Display data for the options comes from Shopify, not the API: the section
   emits a catalog JSON of the Corporate Gifting collection's bundle variants
   (the corporate store's catalog), led by any "Gift option" block overrides
   (title, photos, description, variant id + SKU). mergeOptions keeps
   the API's option_key (what POST expects) and pairs each option with its
   catalog product by handle or bundle SKU; the shared <bundle-contents> list
   then renders "What's inside" from the bundle API, every row opening the
   read-only product drawer.

     POST {api_base}/api/corporate/redeem/:token
       { option_key, ship_date, for_someone_else, recipient: { first_name, last_name, email,
         phone, address1, address2, city, state, zip } }
       -> { ok, redemption } | 409 already redeemed (with redemption) | 410 expired or closed
 */
if (!customElements.get('corporate-gift-redeem')) {
  const SCREENS = ['welcome', 'choose', 'detail', 'details', 'review', 'done', 'expired', 'error'];
  /* Which stepper entry each screen lights up; status screens have none. */
  const STEP_FOR_SCREEN = {
    welcome: 'redeem',
    choose: 'redeem',
    detail: 'redeem',
    details: 'details',
    review: 'details',
    done: 'done',
  };
  const STEP_ORDER = ['redeem', 'details', 'done'];
  const SCREENS_WITH_ACTION_BAR = ['welcome', 'detail', 'details', 'review'];
  const RECIPIENT_FIELDS = ['first_name', 'last_name', 'email', 'phone', 'address1', 'address2', 'city', 'state', 'zip'];
  const REQUIRED_FIELDS = ['first_name', 'last_name', 'address1', 'city', 'state', 'zip'];
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const ZIP_REGEX = /^\d{5}(-\d{4})?$/;
  const PO_BOX_REGEX = /\bp\.?\s*o\.?\s*box\b|\bpost\s+office\s+box\b/i;
  /* Same NANP rules as normalizeUsPhone in the API's corporate/service.js. */
  const NANP_NATIONAL_REGEX = /^[2-9]\d{2}[2-9]\d{6}$/;
  const API_TIMEOUT_MS = 10000;
  const CONVERSION_EVENT = 'corporate_gift_redeemed';
  /* Corporate gift variants are SKU duplicates of the physical box
     (…_BOX_CORP → …_BOX); mirrors physicalSkuForCorporate in the API. */
  const CORPORATE_SKU_SUFFIX = '_CORP';

  const normalizeSku = (raw) => {
    const value = String(raw || '').trim().toUpperCase();
    if (value.length > CORPORATE_SKU_SUFFIX.length && value.endsWith(CORPORATE_SKU_SUFFIX)) {
      return value.slice(0, -CORPORATE_SKU_SUFFIX.length);
    }
    return value;
  };

  /* Bundle products picked in the theme editor (section blocks). */
  const readCatalog = (root) => {
    const script = root.querySelector('[data-cgr-catalog]');
    if (!script) return [];
    try {
      const parsed = JSON.parse(script.textContent || '[]');
      return Array.isArray(parsed) ? parsed.filter((entry) => entry && entry.key) : [];
    } catch (e) {
      return [];
    }
  };

  const optionSkus = (option) => {
    const skus = [];
    (option.items || []).forEach((item) => {
      if (!item) return;
      if (item.sku) skus.push(normalizeSku(item.sku));
      (item.components || []).forEach((c) => c && c.sku && skus.push(normalizeSku(c.sku)));
    });
    return skus;
  };

  const optionVariantIds = (option) => {
    const ids = [];
    const push = (item) => {
      if (!item || item.variant_id == null || item.variant_id === '') return;
      const id = Number(item.variant_id);
      if (Number.isFinite(id)) ids.push(id);
    };
    (option.items || []).forEach((item) => {
      push(item);
      (item.components || []).forEach(push);
    });
    return ids;
  };

  const matchCatalog = (catalog, option) => {
    const key = String(option.key || '').toLowerCase();
    const handle = String(option.handle || '').toLowerCase();
    const skus = optionSkus(option);
    const variantIds = optionVariantIds(option);
    /* A product with several variants has one entry per variant under the
       same handle, so the bundle SKU (or variant id) picks the exact one first. */
    const bySku = catalog.find((entry) => {
      const entrySku = normalizeSku(entry.sku);
      if (entrySku && skus.indexOf(entrySku) !== -1) return true;
      const entryVariant = Number(entry.variant_id);
      return Number.isFinite(entryVariant) && variantIds.indexOf(entryVariant) !== -1;
    });
    if (bySku) return bySku;
    return (
      catalog.find((entry) => {
        const entryHandle = String(entry.handle || entry.key || '').toLowerCase();
        return Boolean(entryHandle) && (entryHandle === key || entryHandle === handle);
      }) || null
    );
  };

  /* Options to show = the campaign's options (API is the authority on which
     gifts this recipient may pick and on option_key), each dressed with its
     Shopify product from the catalog. An API option with no catalog product
     falls back to whatever the API sent. */
  const mergeOptions = (apiOptions, catalog) => {
    const fromCatalog = (entry) => ({
      key: entry.key,
      handle: entry.handle,
      product_id: entry.product_id,
      variant_id: entry.variant_id,
      sku: entry.sku,
      title: entry.title,
      subtitle: entry.subtitle || null,
      badge: entry.badge || null,
      description: entry.description || null,
      image_url: entry.image_url || null,
      images: entry.images || [],
      items: [],
    });
    const list = Array.isArray(apiOptions) ? apiOptions.filter((o) => o && o.key) : [];
    return list.map((option) => {
      const entry = matchCatalog(catalog, option);
      if (!entry) return option;
      const shaped = fromCatalog(entry);
      return Object.assign(shaped, {
        key: option.key,
        subtitle: shaped.subtitle || option.subtitle || null,
        badge: shaped.badge || option.badge || null,
        description: shaped.description || option.description || null,
        image_url: shaped.image_url || option.image_url || null,
        images: shaped.images.length ? shaped.images : option.images || [],
        items: option.items || [],
      });
    });
  };

  /* Same shape as FoxTheme.utils.queryDomNodes (media-gallery, sticky-atc-bar):
     a string selector resolves to one node, a [string] to an array. Local copy
     so this page does not depend on theme.js load order. */
  const queryDomNodes = (selectors, context) =>
    Object.keys(selectors).reduce((nodes, name) => {
      const selector = selectors[name];
      nodes[name] =
        typeof selector === 'string'
          ? context.querySelector(selector)
          : Array.from(context.querySelectorAll(selector[0]));
      return nodes;
    }, {});

  /* Abort instead of spinning forever when the API hangs (bundle-contents.js). */
  const fetchWithTimeout = (url, options, timeoutMs) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return fetch(url, Object.assign({}, options, { signal: controller.signal })).finally(() =>
      clearTimeout(timer)
    );
  };

  const pad2 = (n) => String(n).padStart(2, '0');
  const ymdLocal = (date) =>
    date.getFullYear() + '-' + pad2(date.getMonth() + 1) + '-' + pad2(date.getDate());
  const parseYmd = (ymd) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || '').slice(0, 10));
    return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
  };

  const isValidUsPhone = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '');
    const national = digits.length === 11 && digits.charAt(0) === '1' ? digits.slice(1) : digits;
    return NANP_NATIONAL_REGEX.test(national);
  };

  /* Image URLs from the API should be absolute; never turn a stray value into
     a same-origin path. */
  const safeUrl = (raw) => {
    const value = String(raw || '').trim();
    if (/^\/\//.test(value)) return 'https:' + value;
    if (/^https?:\/\//i.test(value) || /^\//.test(value)) return value;
    return '';
  };

  const formatAddress = (address, { short = false } = {}) => {
    const lines = [[address.first_name, address.last_name].filter(Boolean).join(' ')];
    if (!short) lines.push(address.address1, address.address2);
    const region = [address.state, address.zip].filter(Boolean).join(' ');
    lines.push([address.city, region].filter(Boolean).join(', '));
    return lines.filter(Boolean).join('\n');
  };

  const formatDate = (iso) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatDay = (ymd) => {
    const date = parseYmd(ymd);
    if (!date) return formatDate(ymd);
    return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  };

  const setText = (el, value) => {
    if (el) el.textContent = value == null ? '' : String(value);
  };

  const setImage = (el, src, alt) => {
    if (!el) return;
    const url = safeUrl(src);
    el.alt = alt || '';
    /* Don't use loading=lazy. These screens are hidden when the images are
       created, and Safari never fetches a lazy image that started inside
       [hidden] — the email shows the photo, the page stays blank. */
    el.loading = 'eager';
    if (!url) {
      el.removeAttribute('src');
      return;
    }
    el.src = url;
  };

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  class CorporateGiftRedeem extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        loading: '[data-cgr-loading]',
        stepper: '[data-cgr-stepper]',
        stepperItems: ['[data-cgr-stepper-item]'],
        error: '[data-cgr-error]',
        /* welcome */
        sender: '[data-cgr-sender]',
        message: '[data-cgr-message]',
        companyLogoWrap: '[data-cgr-company-logo-wrap]',
        companyLogo: '[data-cgr-company-logo]',
        /* choose */
        chooseSub: '[data-cgr-choose-sub]',
        deadline: '[data-cgr-deadline]',
        options: '[data-cgr-options]',
        /* detail */
        detailTitle: '[data-cgr-detail-title]',
        detailSubtitle: '[data-cgr-detail-subtitle]',
        detailDescription: '[data-cgr-detail-description]',
        detailToggle: '[data-cgr-detail-toggle]',
        detailContents: '[data-cgr-detail-contents]',
        detailBundleSlot: '[data-cgr-bundle-slot]',
        detailFallback: '[data-cgr-detail-fallback]',
        detailItems: '[data-cgr-detail-items]',
        detailItemsHint: '[data-cgr-detail-items-hint]',
        galleryThumbs: '[data-cgr-gallery-thumbs]',
        galleryImage: '[data-cgr-gallery-image]',
        galleryArrows: ['[data-cgr-gallery-step]'],
        /* delivery details */
        form: '[data-cgr-form]',
        detailsImage: '[data-cgr-details-image]',
        detailsTitle: '[data-cgr-details-title]',
        kindInputs: ['[data-cgr-kind]'],
        kindHints: ['[data-cgr-kind-hint]'],
        emailHints: ['[data-cgr-email-hint]'],
        emailReq: '[data-cgr-email-req]',
        emailOptional: '[data-cgr-email-optional]',
        fieldErrors: ['[data-cgr-error-for]'],
        calendar: '[data-cgr-calendar]',
        calendarGrid: '[data-cgr-cal-grid]',
        calendarMonth: '[data-cgr-cal-month]',
        calendarNavs: ['[data-cgr-cal-step]'],
        calendarSelected: '[data-cgr-cal-selected]',
        /* review */
        reviewImage: '[data-cgr-review-image]',
        reviewTitle: '[data-cgr-review-title]',
        reviewDate: '[data-cgr-review-date]',
        reviewAddress: '[data-cgr-review-address]',
        submitError: '[data-cgr-submit-error]',
        confirmBtn: '[data-cgr-confirm]',
        confirmSpinner: '[data-cgr-confirm-spinner]',
        /* done */
        doneTitle: '[data-cgr-done-title]',
        doneLead: '[data-cgr-done-lead]',
        doneEmailNote: '[data-cgr-done-email-note]',
        doneImage: '[data-cgr-done-image]',
        doneGift: '[data-cgr-done-title-gift]',
        doneDate: '[data-cgr-done-date]',
        doneAddressRow: '[data-cgr-done-address-row]',
        doneAddress: '[data-cgr-done-address]',
      };
    }

    connectedCallback() {
      this.els = queryDomNodes(this.selectors, this);
      this.form = this.els.form;
      this.screens = {};
      SCREENS.forEach((name) => {
        this.screens[name] = this.querySelector('[data-cgr-screen="' + name + '"]');
      });

      /* Bundle products picked in the theme editor; the catalog script and
         the shared <bundle-contents> list sit next to this element. */
      const section = this.closest('.cgr-section') || document;
      this.catalog = readCatalog(section);
      this.section = section;

      /* Campaign payload and what the recipient has picked so far. */
      this.data = null;
      this.option = null;
      this.shipDate = '';
      this.recipient = null;
      this.calendarMonth = '';
      this.galleryIndex = 0;
      this.submitting = false;

      const params = new URLSearchParams(window.location.search);
      this.token = (params.get('token') || '').trim();

      this.addEventListener('click', this.handleClick.bind(this));
      const logoHome = section.querySelector('[data-cgr-logo-home]');
      if (logoHome) logoHome.addEventListener('click', () => this.goToWelcome());
      this.addEventListener('keydown', this.onContentsClick.bind(this));
      this.addEventListener('change', this.handleChange.bind(this));
      this.addEventListener('input', this.handleInput.bind(this));
      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.openReview();
        });
      }

      this.load();
    }

    /* ---------- Data ---------- */

    apiBase() {
      return (this.dataset.apiBase || '').replace(/\/$/, '');
    }

    endpoint() {
      return this.apiBase() + '/api/corporate/redeem/' + encodeURIComponent(this.token);
    }

    async load() {
      if (!this.apiBase()) {
        this.showError(this.t('apiMissing'));
        return;
      }
      if (!this.token) {
        this.showError(this.t('errorNotFound'));
        return;
      }

      try {
        const response = await fetchWithTimeout(
          this.endpoint(),
          { headers: { Accept: 'application/json' } },
          API_TIMEOUT_MS
        );
        if (response.status === 404) {
          this.showError(this.t('errorNotFound'));
          return;
        }
        if (!response.ok) throw new Error('http_' + response.status);
        const payload = await response.json();
        if (!payload || !payload.ok || !payload.redemption) throw new Error('bad_payload');
        this.render(payload.redemption);
      } catch (e) {
        this.showError(this.t('errorGeneric'));
      }
    }

    render(data) {
      this.data = data;
      this.data.options = mergeOptions(data.options, this.catalog);
      if (!this.data.options.length && data.state !== 'redeemed') {
        this.showError(this.t('noOptions'));
        return;
      }
      this.renderWelcome();
      this.renderOptions();
      this.prefillRecipient();

      if (data.state === 'redeemed') {
        this.renderDone(true);
        this.showScreen('done');
      } else if (data.state === 'expired' || data.state === 'closed') {
        this.showScreen('expired');
      } else {
        this.showScreen('welcome');
      }
    }

    findOption(key) {
      return ((this.data && this.data.options) || []).find((option) => option.key === key) || null;
    }

    /* Future ship days the campaign allows, as sorted YYYY-MM-DD strings. */
    allowedDates() {
      const today = ymdLocal(new Date());
      const raw = (this.data && this.data.allowed_ship_dates) || [];
      return raw
        .map((value) => String(value || '').slice(0, 10))
        .filter((value) => parseYmd(value) && value > today)
        .sort();
    }

    allowedMonths() {
      const months = [];
      this.allowedDates().forEach((ymd) => {
        const key = ymd.slice(0, 7);
        if (months.indexOf(key) === -1) months.push(key);
      });
      return months;
    }

    /* ---------- Screens ---------- */

    showScreen(name) {
      if (this.els.loading) this.els.loading.hidden = true;
      SCREENS.forEach((screen) => {
        if (this.screens[screen]) this.screens[screen].hidden = screen !== name;
      });
      this.classList.toggle('has-no-actions', SCREENS_WITH_ACTION_BAR.indexOf(name) === -1);
      this.updateStepper(name);
      /* Error, expired, and closed keep the centered wordmark, not the corner bar.
         Closed is shown on the expired screen. */
      const section = this.closest('.cgr-section');
      if (section) section.classList.toggle('cgr-section--status', name === 'error' || name === 'expired');
      if (name === 'welcome') return;

      /* A step change is a new "page" for assistive tech: scroll up and move
         focus to its heading so the change is announced. */
      window.scrollTo({ top: 0, behavior: 'auto' });
      const heading = this.screens[name] && this.screens[name].querySelector('h1');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
    }

    updateStepper(screen) {
      if (!this.els.stepper) return;
      const step = STEP_FOR_SCREEN[screen];
      this.els.stepper.hidden = !step;
      if (!step) return;

      const activeIndex = STEP_ORDER.indexOf(step);
      this.els.stepperItems.forEach((item) => {
        const index = STEP_ORDER.indexOf(item.dataset.cgrStepperItem);
        const active = index === activeIndex;
        item.classList.toggle('is-active', active);
        item.classList.toggle('is-complete', index < activeIndex || screen === 'done');
        if (active) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
    }

    showError(message) {
      setText(this.els.error, message);
      this.showScreen('error');
    }

    /* ---------- 1. Welcome ---------- */

    renderWelcome() {
      const campaign = this.data.campaign || {};
      setText(this.els.sender, campaign.sender_name || campaign.company);

      const message = String(campaign.gift_message || '').trim();
      setText(this.els.message, message);
      if (this.els.message) this.els.message.hidden = !message;

      const logoSrc = safeUrl(campaign.company_logo_url);
      if (this.els.companyLogoWrap) this.els.companyLogoWrap.hidden = !logoSrc;
      if (logoSrc) setImage(this.els.companyLogo, logoSrc, campaign.company);
    }

    /* ---------- 2. Choose ---------- */

    renderOptions() {
      const recipient = this.data.recipient || {};
      const campaign = this.data.campaign || {};
      setText(
        this.els.chooseSub,
        this.t('giftFrom')
          .replace('__NAME__', recipient.first_name ? recipient.first_name + ', ' : '')
          .replace('__COMPANY__', campaign.company || '')
      );

      const deadline = campaign.redeem_deadline ? formatDate(campaign.redeem_deadline) : '';
      setText(this.els.deadline, deadline ? this.t('deadline').replace('__DATE__', deadline) : '');
      if (this.els.deadline) this.els.deadline.hidden = !deadline;

      if (!this.els.options) return;
      this.els.options.textContent = '';
      (this.data.options || []).forEach((option) => {
        this.els.options.appendChild(this.buildOptionCard(option));
      });
    }

    buildOptionCard(option) {
      const li = el('li', 'cgr-option');
      const button = el('button', 'cgr-option__button');
      button.type = 'button';
      button.dataset.cgrOption = option.key;

      const media = el('div', 'cgr-option__media');
      if (safeUrl(option.image_url)) {
        const img = el('img', 'cgr-option__image');
        setImage(img, option.image_url, this.imageAlt(option));
        media.appendChild(img);
      }
      if (option.badge) media.appendChild(el('span', 'cgr-option__badge', option.badge));

      const body = el('div', 'cgr-option__body');
      body.appendChild(el('span', 'cgr-option__brand', 'Wildgrain'));
      body.appendChild(el('h2', 'cgr-option__title', option.title || ''));
      const excerpt = option.subtitle || option.description;
      if (excerpt) body.appendChild(el('p', 'cgr-option__excerpt', excerpt));
      body.appendChild(el('span', 'cgr-option__cta', this.t('viewGift')));

      button.append(media, body);
      li.appendChild(button);
      return li;
    }

    /* ---------- 3. Detail ---------- */

    openDetail(key) {
      const option = this.findOption(key);
      if (!option) return;
      this.option = option;
      this.galleryIndex = 0;

      setText(this.els.detailTitle, option.title);
      setText(this.els.detailSubtitle, option.subtitle);
      if (this.els.detailSubtitle) this.els.detailSubtitle.hidden = !option.subtitle;

      this.renderDescription(option);
      this.renderContents(option);
      this.renderGallery();
      this.showScreen('detail');
    }

    renderDescription(option) {
      const { detailDescription, detailToggle } = this.els;
      if (!detailDescription) return;
      detailDescription.textContent = '';
      String(option.description || '')
        .split(/\n{2,}/)
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach((part) => detailDescription.appendChild(el('p', null, part)));

      detailDescription.classList.add('is-clamped');
      if (!detailToggle) return;
      detailToggle.textContent = this.t('seeMore');
      /* Only offer "See more" once layout shows the clamp hides something. */
      requestAnimationFrame(() => {
        detailToggle.hidden = detailDescription.scrollHeight <= detailDescription.clientHeight + 2;
      });
    }

    toggleDescription() {
      const { detailDescription, detailToggle } = this.els;
      if (!detailDescription || !detailToggle) return;
      const clamped = detailDescription.classList.toggle('is-clamped');
      detailToggle.textContent = clamped ? this.t('seeMore') : this.t('seeLess');
    }

    /* "What's inside" — the page shares one <bundle-contents> list with the
       corporate catalogue's behaviour: hand it to the detail screen and ask it
       to describe this gift's bundle variant. It fetches the components from
       the bundle API and renders each as a clickable row that opens the
       read-only product drawer (description, nutrition, allergens, baking).
       Boxes with no bundle data hide the list themselves. */
    renderContents(option) {
      const { detailContents, detailBundleSlot, detailFallback, detailItemsHint } = this.els;
      if (!detailContents) return;

      const list = this.bundleList();
      /* bundle-contents looks the box up by SKU; an option without a catalog
         product (not published to the Online Store) still has its bundle SKU
         from the API. */
      const bundleItem = (option.items || []).length === 1 ? option.items[0] : null;
      const sku = option.sku || (bundleItem && bundleItem.sku) || '';
      const variantId = option.variant_id ? String(option.variant_id) : sku;
      if (list && detailBundleSlot && sku) {
        if (detailFallback) detailFallback.hidden = true;
        if (detailItemsHint) detailItemsHint.hidden = false;
        detailContents.hidden = false;
        if (list.parentElement !== detailBundleSlot) detailBundleSlot.appendChild(list);
        /* bundle-contents unhides the slot once it has rows (data-cg-bundle-slot). */
        detailBundleSlot.hidden = true;
        detailBundleSlot.setAttribute('hidden', '');
        const run = () => {
          customElements.upgrade(list);
          if (typeof list.load === 'function') list.load(variantId, sku);
        };
        if (customElements.get('bundle-contents')) run();
        else customElements.whenDefined('bundle-contents').then(run);
        return;
      }

      if (detailBundleSlot) {
        detailBundleSlot.hidden = true;
        detailBundleSlot.setAttribute('hidden', '');
      }
      this.renderFallbackContents(option);
    }

    bundleList() {
      if (this.bundleListEl && this.bundleListEl.isConnected) return this.bundleListEl;
      this.bundleListEl = this.section.querySelector('bundle-contents');
      this.watchBundleSlot();
      return this.bundleListEl;
    }

    /* bundle-contents toggles the slot's [hidden] itself (no rows = hidden).
       Mirror that on the whole block so an empty box shows neither heading
       nor hint. */
    watchBundleSlot() {
      const { detailContents, detailBundleSlot, detailFallback } = this.els;
      if (this.slotObserver || !detailBundleSlot || !detailContents || !window.MutationObserver) return;
      this.slotObserver = new MutationObserver(() => {
        const fallbackShown = detailFallback && !detailFallback.hidden;
        detailContents.hidden = detailBundleSlot.hidden && !fallbackShown;
      });
      this.slotObserver.observe(detailBundleSlot, { attributes: true, attributeFilter: ['hidden'] });
    }

    /* API-only option (no Shopify product picked for it): list the API's
       items; those with a product handle still open the product drawer. */
    renderFallbackContents(option) {
      const { detailContents, detailFallback, detailItems, detailItemsHint } = this.els;
      if (!detailItems) return;
      const items = (option.items || []).filter((item) => item && item.title);
      detailItems.textContent = '';
      let clickable = 0;
      items.forEach((item) => {
        const handle = typeof item.handle === 'string' ? item.handle.trim() : '';
        const li = el('li', 'cgr-detail__item' + (handle ? ' is-clickable' : ''));
        const image = safeUrl(item.image_url);
        if (image) {
          const img = el('img', 'cgr-detail__item-img');
          img.src = image;
          img.alt = '';
          img.width = 56;
          img.height = 56;
          img.loading = 'eager';
          li.appendChild(img);
        }
        const text = el('span', 'cgr-detail__item-text');
        text.append(
          el('span', 'cgr-detail__contents-qty', (Number(item.quantity) || 1) + '\u00d7'),
          el('span', 'cgr-detail__item-name', item.title)
        );
        li.appendChild(text);
        if (handle) {
          clickable += 1;
          li.dataset.handle = handle;
          li.setAttribute('role', 'button');
          li.tabIndex = 0;
          li.setAttribute('aria-label', this.t('viewItem').replace('__TITLE__', item.title));
          li.insertAdjacentHTML(
            'beforeend',
            '<svg class="cgr-detail__item-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"></polyline></svg>'
          );
        }
        detailItems.appendChild(li);
      });
      if (detailItemsHint) detailItemsHint.hidden = clickable === 0;
      if (detailFallback) detailFallback.hidden = items.length === 0;
      detailContents.hidden = items.length === 0;
    }

    onContentsClick(event) {
      const item = event.target.closest('.cgr-detail__item[data-handle]');
      if (!item) return;
      if (event.type === 'keydown') {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
      }
      this.openProductDrawer(item.dataset.handle);
    }

    /* The read-only <quick-view-modal id="BundleQuickView"> in the section
       fetches /products/<handle> through the theme's quick view. Falls back to
       the product page in a new tab when the drawer is not available. */
    openProductDrawer(handle) {
      const url = '/products/' + encodeURIComponent(handle);
      const modal = document.getElementById('BundleQuickView');
      if (modal && typeof modal.show === 'function') {
        modal.dataset.productUrl = url;
        modal.show();
        return;
      }
      window.open(url, '_blank', 'noopener');
    }

    galleryImages() {
      const urls = [];
      [this.option.image_url].concat(this.option.images || []).forEach((raw) => {
        const url = safeUrl(raw);
        if (url && urls.indexOf(url) === -1) urls.push(url);
      });
      return urls;
    }

    renderGallery() {
      const { galleryImage, galleryArrows, galleryThumbs } = this.els;
      const images = this.galleryImages();
      const alt = this.imageAlt(this.option);
      setImage(galleryImage, images[this.galleryIndex], alt);
      galleryArrows.forEach((arrow) => {
        arrow.hidden = images.length <= 1;
      });

      if (!galleryThumbs) return;
      galleryThumbs.textContent = '';
      if (images.length <= 1) return;
      images.forEach((url, index) => {
        const button = el('button', 'cgr-gallery__thumb' + (index === this.galleryIndex ? ' is-active' : ''));
        button.type = 'button';
        button.dataset.cgrGalleryIndex = String(index);
        button.setAttribute('aria-label', alt + ' ' + (index + 1));
        const img = el('img');
        setImage(img, url, '');
        button.appendChild(img);
        const li = el('li');
        li.appendChild(button);
        galleryThumbs.appendChild(li);
      });
    }

    setGalleryIndex(index) {
      const count = this.galleryImages().length;
      if (!count) return;
      this.galleryIndex = ((index % count) + count) % count;
      this.renderGallery();
    }

    /* ---------- 4. Delivery details ---------- */

    openDetails() {
      if (!this.option) return;
      setImage(this.els.detailsImage, this.option.image_url, this.imageAlt(this.option));
      setText(this.els.detailsTitle, this.option.title);
      this.renderCalendar();
      this.showScreen('details');
    }

    field(name) {
      return this.form ? this.form.elements[name] : null;
    }

    fieldValue(name) {
      const input = this.field(name);
      return input ? String(input.value || '').trim() : '';
    }

    recipientKind() {
      const checked = this.els.kindInputs.find((input) => input.checked);
      return checked ? checked.value : 'self';
    }

    /* The invitee's own name/email from the campaign, used as defaults for "Myself". */
    prefillRecipient() {
      const recipient = this.data.recipient || {};
      ['first_name', 'last_name', 'email'].forEach((name) => {
        const input = this.field(name);
        if (input && !input.value) input.value = recipient[name] || '';
      });
      this.refreshRecipientKind();
    }

    /* Switching to "someone else" clears the invitee's details so they are
       not shipped to by mistake; switching back restores the blanks. */
    handleKindChange() {
      const recipient = this.data.recipient || {};
      const other = this.recipientKind() === 'other';
      ['first_name', 'last_name', 'email'].forEach((name) => {
        const input = this.field(name);
        if (!input) return;
        if (other && input.value === (recipient[name] || '')) input.value = '';
        if (!other && !input.value) input.value = recipient[name] || '';
      });
      this.refreshRecipientKind();
    }

    refreshRecipientKind() {
      const kind = this.recipientKind();
      this.els.kindHints.forEach((hint) => {
        hint.hidden = hint.dataset.cgrKindHint !== kind;
      });
      this.els.emailHints.forEach((hint) => {
        hint.hidden = hint.dataset.cgrEmailHint !== kind;
      });
      if (this.els.emailReq) this.els.emailReq.hidden = kind !== 'other';
      if (this.els.emailOptional) this.els.emailOptional.hidden = kind === 'other';
    }

    readRecipient() {
      return RECIPIENT_FIELDS.reduce((recipient, name) => {
        recipient[name] = this.fieldValue(name);
        return recipient;
      }, {});
    }

    /* Returns the recipient when valid, otherwise shows inline errors and
       scrolls to the first one. Mirrors validateManualRecipient on the API. */
    validateRecipient() {
      const recipient = this.readRecipient();
      const errors = {};

      REQUIRED_FIELDS.forEach((name) => {
        if (!recipient[name]) errors[name] = this.t('required');
      });
      if (this.recipientKind() === 'other' && !recipient.email) errors.email = this.t('required');
      if (recipient.email && !EMAIL_REGEX.test(recipient.email)) errors.email = this.t('invalidEmail');
      if (recipient.zip && !ZIP_REGEX.test(recipient.zip)) errors.zip = this.t('invalidZip');
      if (recipient.address1 && PO_BOX_REGEX.test(recipient.address1)) errors.address1 = this.t('poBox');
      if (recipient.phone && !isValidUsPhone(recipient.phone)) errors.phone = this.t('invalidPhone');
      if (!this.shipDate || this.allowedDates().indexOf(this.shipDate) === -1) {
        errors.ship_date = this.t('pickDate');
      }

      this.els.fieldErrors.forEach((errorEl) => {
        this.setFieldError(errorEl.dataset.cgrErrorFor, errors[errorEl.dataset.cgrErrorFor]);
      });

      const first = Object.keys(errors)[0];
      if (!first) return recipient;

      const target = first === 'ship_date' ? this.els.calendar : this.field(first);
      if (target && target.scrollIntoView) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (target && first !== 'ship_date') target.focus({ preventScroll: true });
      return null;
    }

    setFieldError(name, message) {
      const errorEl = this.els.fieldErrors.find((node) => node.dataset.cgrErrorFor === name);
      if (errorEl) {
        errorEl.textContent = message || '';
        errorEl.hidden = !message;
      }
      const input = this.field(name);
      if (input && input.classList) input.classList.toggle('is-invalid', Boolean(message));
    }

    /* Calendar: one month at a time, only campaign ship days enabled. */
    renderCalendar() {
      const { calendarGrid, calendarMonth, calendarNavs, calendarSelected } = this.els;
      if (!calendarGrid || !calendarMonth) return;

      const allowed = this.allowedDates();
      const months = this.allowedMonths();
      if (months.indexOf(this.calendarMonth) === -1) {
        const selectedMonth = this.shipDate.slice(0, 7);
        this.calendarMonth =
          months.indexOf(selectedMonth) !== -1 ? selectedMonth : months[0] || ymdLocal(new Date()).slice(0, 7);
      }

      const [year, month] = this.calendarMonth.split('-').map(Number);
      const firstOfMonth = new Date(year, month - 1, 1);
      calendarMonth.textContent = firstOfMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

      const monthIndex = months.indexOf(this.calendarMonth);
      calendarNavs.forEach((nav) => {
        const next = monthIndex + Number(nav.dataset.cgrCalStep);
        nav.disabled = monthIndex === -1 || next < 0 || next >= months.length;
      });

      calendarGrid.textContent = '';
      for (let i = 0; i < firstOfMonth.getDay(); i += 1) {
        const blank = el('span');
        blank.setAttribute('aria-hidden', 'true');
        calendarGrid.appendChild(blank);
      }
      const today = ymdLocal(new Date());
      const daysInMonth = new Date(year, month, 0).getDate();
      for (let day = 1; day <= daysInMonth; day += 1) {
        const ymd = year + '-' + pad2(month) + '-' + pad2(day);
        calendarGrid.appendChild(this.buildCalendarDay(ymd, day, allowed.indexOf(ymd) !== -1, ymd === today));
      }

      if (!calendarSelected) return;
      calendarSelected.hidden = !this.shipDate;
      if (this.shipDate) {
        calendarSelected.textContent = '';
        calendarSelected.append(
          el('span', null, this.t('shipsOn') + ' '),
          el('strong', null, formatDay(this.shipDate))
        );
      }
    }

    buildCalendarDay(ymd, day, allowed, isToday) {
      const button = el('button', 'cgr-calendar__day', String(day));
      button.type = 'button';
      button.dataset.cgrCalDay = ymd;
      button.setAttribute('role', 'gridcell');
      button.setAttribute('aria-label', formatDay(ymd));
      button.disabled = !allowed;
      if (!allowed) button.setAttribute('aria-disabled', 'true');
      if (isToday) button.classList.add('is-today');
      if (ymd === this.shipDate) {
        button.classList.add('is-selected');
        button.setAttribute('aria-selected', 'true');
      }
      return button;
    }

    stepCalendar(step) {
      const months = this.allowedMonths();
      const next = months.indexOf(this.calendarMonth) + step;
      if (next < 0 || next >= months.length) return;
      this.calendarMonth = months[next];
      this.renderCalendar();
    }

    selectShipDate(ymd) {
      this.shipDate = ymd;
      this.setFieldError('ship_date', '');
      this.renderCalendar();
    }

    /* ---------- 5. Review ---------- */

    openReview() {
      const recipient = this.validateRecipient();
      if (!recipient) return;
      this.recipient = recipient;

      setImage(this.els.reviewImage, this.option.image_url, this.imageAlt(this.option));
      setText(this.els.reviewTitle, this.option.title);
      setText(this.els.reviewDate, formatDay(this.shipDate));
      setText(this.els.reviewAddress, formatAddress(recipient));
      if (this.els.submitError) this.els.submitError.hidden = true;
      this.showScreen('review');
    }

    async confirm() {
      if (this.submitting || !this.option || !this.recipient) return;
      this.setSubmitting(true);

      const body = {
        option_key: this.option.key,
        ship_date: this.shipDate,
        for_someone_else: this.recipientKind() === 'other',
        recipient: this.recipient,
      };

      try {
        const result = await this.submit(body);
        const redemption = result.redemption || {};
        redemption.options = mergeOptions(redemption.options, this.catalog);
        this.data = redemption;
        if (!result.conflict) this.fireConversion();
        this.renderDone(result.conflict);
        this.showScreen('done');
      } catch (e) {
        if (e && e.expired) {
          this.showScreen('expired');
          return;
        }
        if (this.els.submitError) {
          /* 400s carry a recipient-facing message from the API ("Your address
             has an invalid phone number.", "That ship date is not available…"). */
          this.els.submitError.textContent = (e && e.userMessage) || this.t('errorSubmit');
          this.els.submitError.hidden = false;
        }
      } finally {
        this.setSubmitting(false);
      }
    }

    /* Resolves with { redemption, conflict }. A 409 means another tab or a
       forwarded link got there first; the server's record wins and the done
       screen shows that choice instead of this one. */
    async submit(body) {
      const response = await fetchWithTimeout(
        this.endpoint(),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        },
        API_TIMEOUT_MS
      );
      const payload = await response.json().catch(() => null);
      if (response.status === 410) throw Object.assign(new Error('expired'), { expired: true });
      if (response.status === 409 && payload && payload.redemption) {
        return { redemption: payload.redemption, conflict: true };
      }
      if (!response.ok || !payload || !payload.ok || !payload.redemption) {
        const error = new Error((payload && payload.error) || 'http_' + response.status);
        if (response.status === 400 && payload && typeof payload.error === 'string' && /\s/.test(payload.error)) {
          error.userMessage = payload.error;
        }
        throw error;
      }
      return { redemption: payload.redemption, conflict: false };
    }

    setSubmitting(flag) {
      this.submitting = flag;
      if (this.els.confirmBtn) {
        this.els.confirmBtn.disabled = flag;
        this.els.confirmBtn.setAttribute('aria-busy', flag ? 'true' : 'false');
      }
      if (this.els.confirmSpinner) this.els.confirmSpinner.hidden = !flag;
    }

    /* dataLayer push for GA4 / GTM, same as wholesale-lead-form.js. */
    fireConversion() {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: CONVERSION_EVENT,
          gift_option: this.option ? this.option.key : '',
          for_someone_else: this.recipientKind() === 'other',
        });
      } catch (e) {}
    }

    /* ---------- 6. Done ---------- */

    /* `alreadyRedeemed` is the read-only variant for links opened after the
       gift was claimed: softer copy, no address line beyond city/state. */
    renderDone(alreadyRedeemed) {
      const redeemed = this.data.redeemed || {};
      const fromList = this.findOption(redeemed.option_key);
      const kept = this.option && (!redeemed.option_key || this.option.key === redeemed.option_key) ? this.option : null;
      /* The POST payload's option has no theme photos. Keep the card the
         recipient was just looking at when that copy has the image. */
      const option =
        (fromList && safeUrl(fromList.image_url) && fromList) ||
        (kept && safeUrl(kept.image_url) && kept) ||
        fromList ||
        kept ||
        {};
      const address = redeemed.address || this.recipient;
      const { doneTitle, doneLead, doneEmailNote, doneImage, doneGift, doneDate, doneAddressRow, doneAddress } =
        this.els;

      setText(doneTitle, this.t(alreadyRedeemed ? 'alreadyRedeemedTitle' : 'doneTitle'));
      setText(doneLead, this.t(alreadyRedeemed ? 'alreadyRedeemedLead' : 'doneLead'));
      if (doneEmailNote) doneEmailNote.hidden = alreadyRedeemed;

      setImage(doneImage, option.image_url, this.imageAlt(option));
      if (doneImage) doneImage.hidden = !safeUrl(option.image_url);
      setText(doneGift, option.title);
      setText(doneDate, formatDay(redeemed.ship_date || this.shipDate));

      const formatted = address ? formatAddress(address, { short: alreadyRedeemed }) : '';
      setText(doneAddress, formatted);
      if (doneAddressRow) doneAddressRow.hidden = !formatted;
    }

    /* ---------- Events ---------- */

    handleClick(event) {
      if (event.target.closest('.cgr-detail__item[data-handle]')) return this.onContentsClick(event);
      const target = event.target.closest('button, a');
      if (!target) return;

      if (target.hasAttribute('data-cgr-redeem-gift')) return this.showScreen('choose');
      if (target.hasAttribute('data-cgr-redeem-option')) return this.openDetails();
      if (target.hasAttribute('data-cgr-continue')) return this.openReview();
      if (target.hasAttribute('data-cgr-confirm')) return this.confirm();
      if (target.hasAttribute('data-cgr-detail-toggle')) return this.toggleDescription();

      const { cgrOption, cgrBack, cgrGalleryIndex, cgrGalleryStep, cgrCalDay, cgrCalStep } = target.dataset;
      if (cgrOption) return this.openDetail(cgrOption);
      if (cgrBack) return this.goBack(cgrBack);
      if (cgrGalleryIndex != null) return this.setGalleryIndex(Number(cgrGalleryIndex));
      if (cgrGalleryStep) return this.setGalleryIndex(this.galleryIndex + Number(cgrGalleryStep));
      if (cgrCalDay && !target.disabled) return this.selectShipDate(cgrCalDay);
      if (cgrCalStep && !target.disabled) return this.stepCalendar(Number(cgrCalStep));
      return undefined;
    }

    handleChange(event) {
      if (event.target.matches('[data-cgr-kind]')) this.handleKindChange();
    }

    handleInput(event) {
      const name = event.target && event.target.name;
      if (name && this.field(name) === event.target) this.setFieldError(name, '');
    }

    goBack(screen) {
      if (screen === 'details') this.renderCalendar();
      this.showScreen(screen);
    }

    /* Corner wordmark returns to the "who sent you a gift" screen without
       leaving the page, so the recipient's place in the flow is not reloaded away. */
    goToWelcome() {
      if (!this.data || !this.screens.welcome) return;
      this.showScreen('welcome');
      window.scrollTo({ top: 0, behavior: 'auto' });
    }

    /* ---------- Text ---------- */

    imageAlt(option) {
      return this.t('imageOf').replace('__TITLE__', (option && option.title) || '');
    }

    /* Shopify auto-escapes {{ }} and `| escape` on top of that leaves
       literal &#39; in data-text-* attributes (Don't / couldn't). */
    decodeText(value) {
      return String(value || '')
        .replace(/&#39;|&apos;|&#x27;/gi, "'")
        .replace(/&quot;|&#34;|&#x22;/gi, '"')
        .replace(/&amp;/g, '&');
    }

    t(key) {
      const fallbacks = {
        errorNotFound: 'We couldn\u2019t find this gift. Check the link in your email, or contact us.',
        errorGeneric: 'Something went wrong loading your gift. Please try again in a moment.',
        errorSubmit: 'We couldn\u2019t save your choice. Please check your details and try again.',
        apiMissing: 'This page is not configured. Set the API base URL in the theme section settings.',
        noOptions: 'This gift isn\u2019t ready yet. Please contact us and we\u2019ll sort it out.',
        required: 'Required',
        invalidEmail: 'Enter a valid email address.',
        invalidPhone: 'Enter a 10-digit US phone number.',
        invalidZip: 'Enter a 5-digit ZIP code.',
        poBox: 'We can\u2019t deliver frozen boxes to P.O. boxes.',
        pickDate: 'Pick a ship date.',
        seeMore: 'See more',
        seeLess: 'See less',
        giftFrom: '__NAME__here\u2019s what __COMPANY__ picked out for you. Choose one.',
        viewGift: 'View gift \u2192',
        viewItem: 'See details and nutrition for __TITLE__',
        shipsOn: 'Ships on',
        deadline: 'Choose your gift by __DATE__',
        imageOf: 'Photo of __TITLE__',
        doneTitle: 'Your gift is on its way!',
        doneLead: 'We\u2019ve got everything we need. Get ready for your Wildgrain Box!',
        alreadyRedeemedTitle: 'This gift has already been claimed',
        alreadyRedeemedLead: 'Here\u2019s what was chosen. Questions? Our team is happy to help.',
      };
      const attr = 'text' + key.charAt(0).toUpperCase() + key.slice(1);
      return this.decodeText(this.dataset[attr] || fallbacks[key]);
    }
  }

  customElements.define('corporate-gift-redeem', CorporateGiftRedeem);
}
